import { useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from 'react';
import { elementToSurface, surfaceTransformOf } from '../utils/paint';

/**
 * Zoom and pan for the editor surface.
 *
 * Implemented by moving the SVG `viewBox` rather than by transforming a group: the
 * projection then stays in one coordinate space, so hit-testing, marquee selection and
 * drag-to-move all keep working at any zoom without each undoing the transform themselves.
 */

export interface ViewBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Viewport {
  readonly viewBox: ViewBox;
  readonly zoom: number;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly fitToView: () => void;
  readonly zoomAt: (event: WheelEvent<Element>) => void;
  readonly panBy: (deltaX: number, deltaY: number) => void;
  /** Moves the view so a surface point sits in the middle, without changing the zoom. */
  readonly centreOn: (x: number, y: number) => void;
  /**
   * Frames a surface-space rectangle: what "go to this feature" means.
   *
   * Zooms as well as pans, because a found desk centred at the current zoom can still be a
   * four-pixel dot the admin has to hunt for. Padded so the thing framed is not flush
   * against the edge of the view, and never zoomed past the same limits a wheel obeys.
   */
  readonly frame: (box: { x: number; y: number; width: number; height: number }) => void;
  /**
   * What fit-to-view should fit, and the shape of the window it is being fitted into.
   *
   * Reported by the canvas and the editor rather than assumed, because the two numbers that
   * decide it are both outside this hook: the plan occupies part of the surface, and the
   * panel it is drawn in is whatever width the window leaves. Fitting the whole surface into
   * a window of a different shape is what left a wide margin of dead workspace around a plan
   * in a large panel — two margins stacked, one from each fit.
   */
  readonly setFitBox: (box: { x: number; y: number; width: number; height: number }) => void;
  readonly setAspect: (aspect: number) => void;
  /** Screen pixels per surface unit, for keeping stroke widths and handles a constant size. */
  readonly pixelRatio: number;
}

const MINIMUM_ZOOM = 0.5;
const MAXIMUM_ZOOM = 20;
const ZOOM_STEP = 1.25;

/**
 * How hard a wheel notch zooms.
 *
 * Applied to the wheel's own delta rather than as a fixed step, so a trackpad's many small
 * deltas zoom smoothly instead of jumping a quarter at a time, and a mouse wheel's larger
 * notches still move at a useful rate.
 */
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

/** Long enough to read as motion, short enough not to feel like waiting. */
const TWEEN_MILLISECONDS = 180;

/** Ease-out: quick to start, settling at the end, which is what makes a jump read as a move. */
const ease = (progress: number): number => 1 - (1 - progress) ** 3;

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(Math.max(value, minimum), maximum);

/** How much room is left around a fitted plan, as a fraction of its size. */
const FIT_MARGIN = 0.04;

export const useViewport = (width: number, height: number): Viewport => {
  const fullView = useMemo<ViewBox>(() => ({ x: 0, y: 0, width, height }), [width, height]);
  const [viewBox, setViewBox] = useState<ViewBox>(fullView);
  const animation = useRef<number | undefined>();

  // The plan's own extent on the surface, and the shape of the window. Both start as the
  // whole surface, which is what they were before either was measured.
  const fitBox = useRef<ViewBox>(fullView);
  const aspect = useRef(width / height);

  const zoom = width / viewBox.width;

  // Any direct manipulation — a pan, a wheel — cancels a running tween, so the surface never
  // fights the pointer.
  const stopAnimation = useCallback(() => {
    if (animation.current !== undefined) {
      cancelAnimationFrame(animation.current);
      animation.current = undefined;
    }
  }, []);

  useEffect(() => stopAnimation, [stopAnimation]);

  /** Eases to a target box, for the discrete actions: the zoom buttons and fit-to-view. */
  const animateTo = useCallback(
    (target: ViewBox) => {
      stopAnimation();

      const start = performance.now();
      let from: ViewBox | undefined;

      const step = (now: number): void => {
        const progress = Math.min(1, (now - start) / TWEEN_MILLISECONDS);
        const eased = ease(progress);

        setViewBox((current) => {
          from ??= current;

          return {
            x: from.x + (target.x - from.x) * eased,
            y: from.y + (target.y - from.y) * eased,
            width: from.width + (target.width - from.width) * eased,
            height: from.height + (target.height - from.height) * eased,
          };
        });

        animation.current = progress < 1 ? requestAnimationFrame(step) : undefined;
      };

      animation.current = requestAnimationFrame(step);
    },
    [stopAnimation],
  );

  const zoomTo = useCallback(
    (nextZoom: number, focusX: number, focusY: number) => {
      stopAnimation();

      setViewBox((current) => {
        const clampedZoom = clamp(nextZoom, MINIMUM_ZOOM, MAXIMUM_ZOOM);
        const nextWidth = width / clampedZoom;
        const nextHeight = height / clampedZoom;

        // Keep whatever sits under the focus point where it is, so zooming feels anchored
        // to the cursor rather than to the corner of the surface.
        const ratioX = (focusX - current.x) / current.width;
        const ratioY = (focusY - current.y) / current.height;

        return {
          x: focusX - ratioX * nextWidth,
          y: focusY - ratioY * nextHeight,
          width: nextWidth,
          height: nextHeight,
        };
      });
    },
    [width, height, stopAnimation],
  );

  /** Zooms about the middle of the view, which is what a button press means. */
  const zoomAboutCentre = useCallback(
    (factor: number) => {
      const nextZoom = clamp((width / viewBox.width) * factor, MINIMUM_ZOOM, MAXIMUM_ZOOM);
      const nextWidth = width / nextZoom;
      const nextHeight = height / nextZoom;

      animateTo({
        x: viewBox.x + (viewBox.width - nextWidth) / 2,
        y: viewBox.y + (viewBox.height - nextHeight) / 2,
        width: nextWidth,
        height: nextHeight,
      });
    },
    [width, height, viewBox, animateTo],
  );

  const zoomIn = useCallback(() => zoomAboutCentre(ZOOM_STEP), [zoomAboutCentre]);

  const zoomOut = useCallback(() => zoomAboutCentre(1 / ZOOM_STEP), [zoomAboutCentre]);

  /**
   * The box to show so the plan fills the window.
   *
   * Grown to the window's aspect rather than the plan's: a viewBox of a different shape is
   * letterboxed by the painter, and the bands it leaves are exactly the empty margin this
   * exists to remove. Growing never crops — the plan stays whole, and the extra is workspace.
   */
  const fittedView = useCallback((): ViewBox => {
    const box = fitBox.current;
    const marginX = box.width * FIT_MARGIN;
    const marginY = box.height * FIT_MARGIN;

    const padded = {
      x: box.x - marginX,
      y: box.y - marginY,
      width: box.width + marginX * 2,
      height: box.height + marginY * 2,
    };

    const windowAspect = aspect.current > 0 ? aspect.current : padded.width / padded.height;
    const boxAspect = padded.width / padded.height;

    if (boxAspect > windowAspect) {
      const grown = padded.width / windowAspect;
      return { ...padded, y: padded.y - (grown - padded.height) / 2, height: grown };
    }

    const grown = padded.height * windowAspect;
    return { ...padded, x: padded.x - (grown - padded.width) / 2, width: grown };
  }, []);

  const fitToView = useCallback(() => animateTo(fittedView()), [fittedView, animateTo]);

  const setFitBox = useCallback((box: ViewBox) => {
    fitBox.current = box;
  }, []);

  const setAspect = useCallback((next: number) => {
    if (next > 0 && Number.isFinite(next)) {
      aspect.current = next;
    }
  }, []);

  const centreOn = useCallback(
    (x: number, y: number) => {
      stopAnimation();
      setViewBox((current) => ({
        ...current,
        x: x - current.width / 2,
        y: y - current.height / 2,
      }));
    },
    [stopAnimation],
  );

  const frame = useCallback(
    (box: { x: number; y: number; width: number; height: number }) => {
      // A point feature — a desk, a label — has no size to frame, so the box is padded to
      // something the eye can land on before the zoom is worked out. Without this, framing a
      // desk divides by zero and lands at maximum zoom on an empty patch of floor.
      const padded = {
        width: Math.max(box.width, width / 12),
        height: Math.max(box.height, height / 12),
      };

      const targetZoom = clamp(
        Math.min(width / (padded.width * 1.6), height / (padded.height * 1.6)),
        MINIMUM_ZOOM,
        MAXIMUM_ZOOM,
      );

      const nextWidth = width / targetZoom;
      const nextHeight = height / targetZoom;

      animateTo({
        x: box.x + box.width / 2 - nextWidth / 2,
        y: box.y + box.height / 2 - nextHeight / 2,
        width: nextWidth,
        height: nextHeight,
      });
    },
    [width, height, animateTo],
  );

  const zoomAt = useCallback(
    (event: WheelEvent<Element>) => {
      const surface = event.currentTarget.getBoundingClientRect();
      const currentZoom = width / viewBox.width;
      // Proportional to how far the wheel actually turned, so a trackpad glides and a mouse
      // notch still steps at a useful rate.
      const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY);

      // Through the same transform the surface is drawn with. Mapping the element linearly
      // onto the viewBox instead ignores the letterboxing, so the point the zoom anchors to
      // drifts from the one under the cursor the further out it is.
      const focus = elementToSurface(
        { x: event.clientX - surface.left, y: event.clientY - surface.top },
        surfaceTransformOf(surface.width, surface.height, viewBox),
      );

      zoomTo(currentZoom * factor, focus.x, focus.y);
    },
    [width, viewBox, zoomTo],
  );

  const panBy = useCallback((deltaX: number, deltaY: number) => {
    stopAnimation();
    setViewBox((current) => ({ ...current, x: current.x + deltaX, y: current.y + deltaY }));
  }, [stopAnimation]);

  return {
    viewBox,
    zoom,
    zoomIn,
    zoomOut,
    fitToView,
    zoomAt,
    panBy,
    centreOn,
    frame,
    setFitBox,
    setAspect,
    pixelRatio: 1 / zoom,
  };
};
